import { useState } from 'react';
import { api } from '../lib/api';
import { fmtAgo, fmtBytes, fmtDate, useApi } from '../lib/hooks';
import { KIND_LABEL, parseGoogleUrl } from '../../shared/gdocs';
import { Icon } from '../components/Icon';
import { GoogleDocView, type DocView } from '../components/GoogleDoc';
import { Badge, Button, ErrorBox, Field, Loading, Modal, Panel, Toggle, useAction } from '../components/ui';

interface Scope { id: string; name: string; section: string | null }
interface Resp { documents: DocView[]; scopes: Scope[]; job: { lastRunAt?: string; lastResult?: string } | null }

const REFRESH = [[10, '10 minutes'], [30, '30 minutes'], [60, '1 hour'], [180, '3 hours'], [720, '12 hours'], [1440, '24 hours'], [10080, '1 week']] as const;

function DocStatus({ d }: { d: DocView }) {
  if (!d.enabled) return <Badge>off</Badge>;
  if (d.status === 'embed-only') return <Badge tone="blue" title="Shown directly from Google; nothing to import">embedded</Badge>;
  if (d.status === 'ok') return <Badge tone="green"><Icon name="check" size={11} /> imported</Badge>;
  if (d.status === 'pending') return <Badge tone="amber">waiting</Badge>;
  return <Badge tone="red" title={d.error ?? ''}>error</Badge>;
}

/** Admin → Data Sources: the Ravenbound Google Docs library (platform documents and each tool's). */
export function GoogleDocsPanel({ manage }: { manage: boolean }) {
  const { data, error, loading, reload } = useApi<Resp>('/api/admin/documents');
  const { busy, run } = useAction();
  const [edit, setEdit] = useState<Partial<DocView> | null>(null);
  const [preview, setPreview] = useState<DocView | null>(null);

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} retry={reload} />;

  const scopeName = (id: string) => data.scopes.find((s) => s.id === id)?.name ?? id;
  const groups = new Map<string, DocView[]>();
  for (const d of [...data.documents].sort((a, b) => a.title.localeCompare(b.title))) groups.set(d.scope, [...(groups.get(d.scope) ?? []), d]);
  const order = [...groups.keys()].sort((a, b) => a === 'platform' ? -1 : b === 'platform' ? 1 : scopeName(a).localeCompare(scopeName(b)));

  return (
    <Panel icon="file" title="Ravenbound Google Docs" actions={manage && <Button variant="primary" icon="plus" onClick={() => setEdit({ scope: 'platform', display: 'formatted', refreshMinutes: 60 })}>Add document</Button>}>
      <p className="muted" style={{ marginTop: 0 }}>
        Google Docs, Sheets, Slides and Forms the dashboard uses. <strong>Platform</strong> documents are available everywhere; tool documents belong to one tool (unit leaders can also add documents to their own unit page).
        Sheets and Docs are imported on a schedule so they can be shown in dashboard style; everything else is embedded straight from Google.
      </p>
      {order.length === 0 && <p className="muted">No documents yet.</p>}
      {order.map((scope) => (
        <div key={scope} className="gdocs-group">
          <h4 className="group-title">{scope === 'platform' ? 'Platform' : scopeName(scope)} <span className="muted">{groups.get(scope)!.length}</span></h4>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Document</th><th>Type</th><th>Status</th><th>Imported</th><th className="right">Size</th><th>On</th><th /></tr></thead>
              <tbody>
                {groups.get(scope)!.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <strong>{d.title}</strong>{d.purpose && !d.removable && <> <Badge tone="gold" title={`Used by the tool (${d.purpose}); can’t be removed`}>tool source</Badge></>}
                      {d.purpose && d.removable && <> <Badge tone="neutral" title="Built in, but no longer needed; you can remove it">built in</Badge></>}
                      {d.description && <div className="muted small">{d.description}</div>}
                      {d.error && <div className="bad-text small">{d.error}</div>}
                      <div className="muted small">Added by {d.addedByName || 'system'}</div>
                    </td>
                    <td className="nowrap">{KIND_LABEL[d.kind]}<div className="muted small">{d.display === 'formatted' ? 'Dashboard style' : 'As in Google'}</div></td>
                    <td><DocStatus d={d} /></td>
                    <td className="nowrap">{d.lastFetchedAt ? <span title={fmtDate(d.lastFetchedAt)}>{fmtAgo(d.lastFetchedAt)}</span> : '—'}
                      {d.status !== 'embed-only' && <div className="muted small">every {REFRESH.find(([v]) => v === d.refreshMinutes)?.[1] ?? `${d.refreshMinutes} min`}</div>}</td>
                    <td className="right nowrap">{d.sizeBytes ? fmtBytes(d.sizeBytes) : '—'}</td>
                    <td><Toggle checked={d.enabled} disabled={!manage} label={`Enable ${d.title}`} onChange={(v) => run(`en${d.id}`, async () => { await api.patch(`/api/admin/documents/${d.id}`, { enabled: v }); await reload(); })} /></td>
                    <td>
                      <div className="row-actions">
                        <Button variant="ghost" icon="eye" aria-label={`Preview ${d.title}`} title="Preview" onClick={() => setPreview(d)} />
                        <a className="icon-btn" href={d.url} target="_blank" rel="noreferrer noopener" title="Open in Google"><Icon name="external" size={15} /></a>
                        {manage && d.status !== 'embed-only' && <Button variant="ghost" icon="refresh" aria-label={`Import ${d.title} now`} title="Import now" busy={busy === `r${d.id}`}
                          onClick={() => run(`r${d.id}`, async () => { const r = await api.post<DocView>(`/api/admin/documents/${d.id}/refresh`); await reload(); if (r.status === 'error') throw new Error(r.error ?? 'Import failed'); }, `${d.title} imported`)} />}
                        {manage && <Button variant="ghost" icon="edit" aria-label={`Edit ${d.title}`} onClick={() => setEdit(d)} />}
                        {manage && (!d.purpose || d.removable) && <Button variant="ghost" icon="trash" aria-label={`Remove ${d.title}`} busy={busy === `d${d.id}`}
                          onClick={() => confirm(`Remove "${d.title}"? The Google document itself isn’t touched.`) && run(`d${d.id}`, async () => { await api.del(`/api/admin/documents/${d.id}`); await reload(); }, 'Document removed')} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <p className="muted small" style={{ marginBottom: 0 }}>
        Documents must be shared as “Anyone with the link can view”, or published with File → Share → Publish to web.
        {data.job?.lastRunAt && <> Scheduled import last ran {fmtAgo(data.job.lastRunAt)}{data.job.lastResult && data.job.lastResult !== 'ok' ? ` (${data.job.lastResult})` : ''}.</>}
      </p>
      {edit && <DocModal doc={edit} scopes={data.scopes} onClose={() => setEdit(null)} onSaved={reload} />}
      {preview && (
        <Modal title={preview.title} wide onClose={() => setPreview(null)}>
          <GoogleDocView doc={preview} height={560} />
        </Modal>
      )}
    </Panel>
  );
}

function DocModal({ doc, scopes, onClose, onSaved }: { doc: Partial<DocView>; scopes: Scope[]; onClose: () => void; onSaved: () => Promise<unknown> | void }) {
  const [d, setD] = useState(doc);
  const { busy, run } = useAction();
  const parsed = d.url ? parseGoogleUrl(d.url) : null;
  const canStyle = parsed?.kind === 'sheet' || parsed?.kind === 'doc';
  const sections = new Map<string, Scope[]>();
  for (const s of scopes.filter((x) => x.id !== 'platform')) sections.set(s.section ?? 'other', [...(sections.get(s.section ?? 'other') ?? []), s]);
  const save = () => run('save', async () => {
    const body = { title: d.title, url: d.url, description: d.description ?? '', display: canStyle ? d.display : 'embed', scope: d.scope, refreshMinutes: d.refreshMinutes };
    if (d.id) await api.patch(`/api/admin/documents/${d.id}`, body); else await api.post('/api/admin/documents', body);
    await onSaved();
    onClose();
  }, 'Document saved');

  return (
    <Modal title={d.id ? 'Edit document' : 'Add a Google document'} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy === 'save'} disabled={!d.title?.trim() || !parsed} onClick={save}>Save</Button></>}>
      <Field label="Google link" hint={d.url && !parsed ? 'That doesn’t look like a Google Docs, Sheets, Slides, Forms or Drive link.' : 'A share link or a “Publish to web” link.'}>
        <input value={d.url ?? ''} onChange={(e) => setD({ ...d, url: e.target.value })} placeholder="https://docs.google.com/…" autoFocus disabled={!!d.purpose} />
      </Field>
      {parsed && <p className="small"><Badge tone="blue">{KIND_LABEL[parsed.kind]}</Badge> {parsed.published ? 'Published to web' : 'Shared link'}{!canStyle && ' · will be embedded'}</p>}
      <Field label="Title"><input value={d.title ?? ''} onChange={(e) => setD({ ...d, title: e.target.value })} maxLength={120} /></Field>
      <Field label="Description"><input value={d.description ?? ''} onChange={(e) => setD({ ...d, description: e.target.value })} maxLength={500} /></Field>
      <Field label="Used by" hint="Platform documents are available to every tool. Tool documents only appear in that tool.">
        <select value={d.scope ?? 'platform'} onChange={(e) => setD({ ...d, scope: e.target.value })} disabled={!!d.purpose}>
          <option value="platform">Platform</option>
          {[...sections.entries()].map(([sec, list]) => (
            <optgroup key={sec} label={sec[0].toUpperCase() + sec.slice(1)}>
              {list.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          ))}
        </select>
      </Field>
      <div className="form-row">
        {canStyle && (
          <Field label="Show it">
            <select value={d.display ?? 'formatted'} onChange={(e) => setD({ ...d, display: e.target.value as 'embed' | 'formatted' })}>
              <option value="formatted">Dashboard style</option>
              <option value="embed">As in Google</option>
            </select>
          </Field>
        )}
        {canStyle && (
          <Field label="Import every">
            <select value={d.refreshMinutes ?? 60} onChange={(e) => setD({ ...d, refreshMinutes: Number(e.target.value) })}>
              {REFRESH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        )}
      </div>
    </Modal>
  );
}
